import React, { useState } from 'react';
import { useLocation, Link, useNavigate } from 'react-router-dom';
import Container from '../components/common/Container';
import { Heading, Subheading } from '../components/common/TextComponents';
import Button from '../components/common/Button';
import { Logo } from '../components/common/Logo';
import { useSignup } from '../hooks/useSignup';
import { useAuthContext } from '../hooks/useAuthContext';

const EnhancedConfirmationPage = () => {
  const location = useLocation();
  const navigate = useNavigate();
  const { signup, error: signupError, isPending } = useSignup();
  const { user } = useAuthContext();
  
  // Get user data from location state
  const userFormData = location.state?.userFormData || {};
  const submissionId = location.state?.submissionId || '';
  const email = userFormData.email || '';
  const firstName = userFormData.firstName || '';
  const lastName = userFormData.lastName || '';
  
  // State for password input
  const [password, setPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [accountCreated, setAccountCreated] = useState(false);
  const [formError, setFormError] = useState('');
  
  // Handle form submission
  const handleCreateAccount = async (e) => {
    e.preventDefault();
    setFormError('');
    
    // Validate passwords
    if (password !== confirmPassword) {
      setFormError('Passwords do not match');
      return;
    }
    
    if (password.length < 6) {
      setFormError('Password must be at least 6 characters');
      return;
    }
    
    // Create account
    const displayName = `${firstName} ${lastName}`.trim();
    const result = await signup(email, password, displayName, submissionId);
    
    if (result.success) {
      setAccountCreated(true);
      
      // Redirect to dashboard after a short delay
      setTimeout(() => {
        navigate('/dashboard');
      }, 2000);
    }
  };
  
  return (
    <div className="py-24 relative min-h-screen sm:py-32">
      {/* Gradient background */}
      <div className="absolute inset-0 bg-gradient-to-b from-amber-100 via-pink-300 to-purple-500 opacity-10 -z-10"></div>
      
      <Container>
        <div className="mx-auto max-w-2xl text-center relative">
          <div className="mb-8 flex justify-center">
            <Logo className="h-12" />
          </div>
          
          <div className="w-16 h-16 bg-green-100 rounded-full flex items-center justify-center mx-auto mb-6">
            <svg xmlns="http://www.w3.org/2000/svg" className="h-8 w-8 text-green-600" fill="none" viewBox="0 0 24 24" stroke="currentColor">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
            </svg>
          </div>
          
          <Heading as="h1" className="mt-2">
            Your profile is now complete!
          </Heading>
          
          <p className="mt-6 text-xl text-gray-600">
            Thank you for creating your Burnt profile. Set up your account to see your personalized career insights!
          </p>
          
          {user || accountCreated ? (
            <div className="mt-10 p-6 bg-amber-50 rounded-xl border border-amber-100">
              <Subheading>Your account is ready!</Subheading>
              <p className="mt-3 text-lg text-gray-800">
                We'll be sending personalized job matches to <span className="font-semibold">{email}</span> within the next 48 hours.
              </p>
              
              <div className="mt-10">
                <Link to="/network">
                  <Button>
                    Go to Network
                  </Button>
                </Link>
              </div>
            </div>
          ) : (
            <div className="mt-10 p-6 bg-white rounded-xl border border-gray-200 shadow-md">
              <Subheading>Create your account</Subheading>
              <p className="mt-3 text-lg text-gray-800 mb-6">
                Set up your account to view your personalized career insights.
              </p>
              
              <form onSubmit={handleCreateAccount} className="space-y-4 text-left">
                <div>
                  <label htmlFor="email" className="block text-sm font-medium text-gray-700 mb-1">Email</label>
                  <input
                    type="email"
                    id="email"
                    value={email}
                    disabled
                    className="w-full px-3 py-2 border border-gray-300 rounded-md bg-gray-100"
                  />
                </div>
                
                <div>
                  <label htmlFor="password" className="block text-sm font-medium text-gray-700 mb-1">Password</label>
                  <input
                    type="password"
                    id="password"
                    value={password}
                    onChange={(e) => setPassword(e.target.value)}
                    required
                    className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-amber-500"
                  />
                </div>
                
                <div>
                  <label htmlFor="confirmPassword" className="block text-sm font-medium text-gray-700 mb-1">Confirm Password</label>
                  <input
                    type="password"
                    id="confirmPassword"
                    value={confirmPassword}
                    onChange={(e) => setConfirmPassword(e.target.value)}
                    required
                    className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-amber-500"
                  />
                </div>
                
                {(formError || signupError) && (
                  <div className="p-3 bg-red-50 text-red-700 rounded-md">
                    {formError || signupError}
                  </div>
                )}
                
                <div className="mt-6">
                  <Button
                    type="submit"
                    disabled={isPending}
                    className={isPending ? 'opacity-70 cursor-not-allowed' : ''}
                  >
                    {isPending ? 'Creating Account...' : 'Create Account & View Results'}
                  </Button>
                </div>
              </form>
              
              <div className="mt-4 text-sm text-gray-500">
                <p>By creating an account, you agree to our Terms of Service and Privacy Policy.</p>
              </div>
            </div>
          )}
          
          <div className="mt-10">
            <Link to="/">
              <Button variant="secondary">
                Return to Homepage
              </Button>
            </Link>
          </div>
        </div>
      </Container>
    </div>
  );
};

export default EnhancedConfirmationPage;